import { Button, ContextMenu, LoadIndicator, Tooltip } from 'devextreme-react';
import './TraceabilityMasiveActions.scss';
import DataGrid, { Toolbar, Item, SearchPanel, Column, Paging, Pager, Button as ButtonGrid, LoadPanel, HeaderFilter } from 'devextreme-react/data-grid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filter, whatsapp, resButtonsMail, runTask } from '../../images/icons';
import { ModalFilter } from './components/ModalFilter';
import { CustomFilters, TypeTableFilter } from '../../types/types.system';
import { useApiTrace } from '../../api/api.trace';
import { UseApiResults } from '../../api/api.results';
import { useApiOrders } from '../../api/api.orders';
import moment from 'moment';
import { EMonitoreoServiciosMasivos, SysI_MonitoreoServiciosMasivos } from '../../types/types.monitoreo';
import { PopupInformation } from '../../components/utils/popup-information/PopupInformation';
import { useAppSelector } from '../../contexts/hooks';
import { selectModuleEmail, selectModuleExternalRemissions, selectModuleWhatsApp } from '../../contexts/selectors';
import { newToast, ModalConfirmation } from '../../components';
import { RightPanelControlled } from '../../components/utils/right-panel-controlled/RighPanel';
import { OrderInfo, PatientInfo } from '../../components/library/order-patient-info';
import { ConvertDataOrder, ConvertDataPatient } from '../../utils/functions';

const LstTipos = [
  { Id: EMonitoreoServiciosMasivos.Email, Nombre: 'Envío masivo de correo' },
  { Id: EMonitoreoServiciosMasivos.WhatsApp, Nombre: 'Envío masivo por WhatsApp' },
  { Id: EMonitoreoServiciosMasivos.RemisionExterna, Nombre: 'Envío masivo de remisiones' },
];

const buildDefaultFilters = (): CustomFilters[] => [
  {
    Field: 'Mtsm_Fecha',
    TypeFilter: TypeTableFilter.Between,
    Value: moment().startOf('day').format(),
    Value2: moment().endOf('day').format(),
  },
];

const Index = () => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const { getDataForMasiveTask } = useApiTrace();
  const { sendResultMasiveByEmail, sendResultMasiveByWhatsApp, sendResultMasiveExternalRemission } = UseApiResults();
  const { getHeaderOrderById, findOrder } = useApiOrders();
  const [gridFilters, setGridFilters] = useState<CustomFilters[]>(buildDefaultFilters());
  const [data, setData] = useState<SysI_MonitoreoServiciosMasivos[]>([]);
  const gridRef = useRef<DataGrid>(null);
  const [selectedItem, setSelectedItem] = useState<SysI_MonitoreoServiciosMasivos>();

  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [currentPatient, setCurrentPatient] = useState<any>(null);
  const [loadingPanel, setLoadingPanel] = useState<boolean>(false);
  const [loadingFindOrder, setLoadingFindOrder] = useState<boolean>(false);
  const [confirmTask, setConfirmTask] = useState<{ visible: boolean; tipo?: EMonitoreoServiciosMasivos; text?: string }>({ visible: false });
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

  const moduleEmail = useAppSelector(selectModuleEmail);
  const moduleWhatsApp = useAppSelector(selectModuleWhatsApp);
  const moduleExternalRemissions = useAppSelector(selectModuleExternalRemissions);

  useEffect(() => {
    setFilter();
  }, [gridFilters]);

  const setFilter = useCallback(() => {
    try {
      gridRef.current?.instance.beginCustomLoading('');
      getDataForMasiveTask(gridFilters).then((res) => {
        setData(res);
      });
    } finally {
      gridRef.current?.instance.endCustomLoading();
    }
  }, [gridFilters]);

  const handleApplyFilter = useCallback(
    (data) => {
      const filterTest: CustomFilters[] = [];
      Object.keys(data).forEach((item) => {
        if (data[item] !== null && data[item] !== undefined && data[item] !== '') {
          switch (item) {
            case 'Mtsm_Fecha':
              if (data[item][0] && data[item][1]) {
                filterTest.push({
                  Value: moment(data[item][0]).format(),
                  Value2: moment(data[item][1]).format(),
                  Field: item,
                  TypeFilter: TypeTableFilter.Between,
                });
              }
              break;
            case 'Mtsm_Estado':
              filterTest.push({ Value: data[item] ? 'true' : 'false', Field: item, TypeFilter: TypeTableFilter.Equal });
              break;
            default:
              filterTest.push({ Value: data[item], Field: item, TypeFilter: TypeTableFilter.Equal });
              break;
          }
        }
      });
      setGridFilters(filterTest);
      setModalVisible(false);
    },
    [setModalVisible, setGridFilters]
  );

  const handleRefresh = useCallback(() => {
    setFilter();
  }, [setFilter]);

  const handleModalHideFilter = useCallback(() => {
    setModalVisible(false);
  }, [setModalVisible]);

  const handleModalShowFilter = useCallback(() => {
    setModalVisible(true);
  }, [setModalVisible]);

  const handlePreview = useCallback((e) => {
    const data: SysI_MonitoreoServiciosMasivos = e.row.data;
    setSelectedItem(data);
  }, []);

  const runMenuItems = useMemo(() => {
    const items: { tipo: EMonitoreoServiciosMasivos; text: string; icon: string }[] = [];
    if (moduleEmail) items.push({ tipo: EMonitoreoServiciosMasivos.Email, text: 'Enviar resultados por correo masivamente', icon: resButtonsMail });
    if (moduleWhatsApp) items.push({ tipo: EMonitoreoServiciosMasivos.WhatsApp, text: 'Enviar resultados por WhatsApp masivamente', icon: whatsapp });
    if (moduleExternalRemissions) items.push({ tipo: EMonitoreoServiciosMasivos.RemisionExterna, text: 'Enviar resultados de remisiones externas', icon: 'export' });
    return items;
  }, [moduleEmail, moduleWhatsApp, moduleExternalRemissions]);

  const handleRunTask = useCallback((e) => {
    const tipo: EMonitoreoServiciosMasivos = e.itemData?.tipo;
    const text: string = e.itemData?.text;
    if (!tipo) return;
    setConfirmTask({ visible: true, tipo, text });
  }, []);

  const handleCancelConfirmTask = useCallback(() => {
    if (confirmLoading) return;
    setConfirmTask({ visible: false });
  }, [confirmLoading]);

  const handleConfirmTask = useCallback(async () => {
    const tipo = confirmTask.tipo;
    if (!tipo) return;
    setConfirmLoading(true);
    try {
      gridRef.current?.instance.beginCustomLoading('');
      let result: any = null;
      switch (tipo) {
        case EMonitoreoServiciosMasivos.Email:
          result = await sendResultMasiveByEmail();
          break;
        case EMonitoreoServiciosMasivos.WhatsApp:
          result = await sendResultMasiveByWhatsApp();
          break;
        case EMonitoreoServiciosMasivos.RemisionExterna:
          result = await sendResultMasiveExternalRemission();
          break;
      }
      if (result !== null && result?.internalCode <= 199) {
        newToast('SUCCESS', result?.message || 'Tarea ejecutada correctamente', 5);
        setFilter();
      }
    } finally {
      gridRef.current?.instance.endCustomLoading();
      setConfirmLoading(false);
      setConfirmTask({ visible: false });
    }
  }, [confirmTask.tipo, sendResultMasiveByEmail, sendResultMasiveByWhatsApp, sendResultMasiveExternalRemission, setFilter]);

  const handleOrderClick = useCallback(
    async (orderId: number) => {
      if (!orderId) return;
      setIsPanelOpen(true);
      setLoadingPanel(true);
      try {
        const res = await getHeaderOrderById(orderId);
        if (res) {
          const { Orden, Paciente } = res;
          setCurrentOrder(ConvertDataOrder(Orden));
          setCurrentPatient(ConvertDataPatient(Paciente));
        } else {
          setCurrentOrder(null);
          setCurrentPatient(null);
          newToast('WARNING', 'No se encontró información de la orden', 3);
        }
      } catch (error) {
        newToast('ERROR', 'Error al cargar los datos de la orden', 3);
        setCurrentOrder(null);
        setCurrentPatient(null);
      } finally {
        setLoadingPanel(false);
      }
    },
    [getHeaderOrderById]
  );

  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
    setCurrentOrder(null);
    setCurrentPatient(null);
  }, []);

  const handleClickViewOrder = useCallback(() => {
    if (currentOrder) {
      findOrder(undefined, currentOrder.Ord_Id, setLoadingFindOrder, true);
    }
  }, [currentOrder, findOrder]);

  const renderTipoCell = useCallback((cellData) => {
    const tipo: EMonitoreoServiciosMasivos = cellData.value;
    const id: number = cellData.data?.Mtsm_Id;
    const targetId = `traceMasiveTipo-${id}`;
    let icon: JSX.Element | null = null;
    let label = '';
    switch (tipo) {
      case EMonitoreoServiciosMasivos.Email:
        icon = <img src={resButtonsMail} alt='email' />;
        label = 'Envío masivo de correo';
        break;
      case EMonitoreoServiciosMasivos.WhatsApp:
        icon = <img src={whatsapp} alt='whatsapp' />;
        label = 'Envío masivo por WhatsApp';
        break;
      case EMonitoreoServiciosMasivos.RemisionExterna:
        icon = <i className='dx-icon dx-icon-export' style={{ fontSize: 22 }} />;
        label = 'Envío masivo de remisiones';
        break;
    }
    return (
      <div id={targetId} className='trace-masive-tipo-cell'>
        {icon}
        <Tooltip target={`#${targetId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
          {label}
        </Tooltip>
      </div>
    );
  }, []);

  const renderEstadoCell = useCallback((cellData) => {
    const estado: boolean = cellData.value;
    return <span className={`chip ${estado ? 'chip-ok' : 'chip-warn'}`}>{estado ? 'Correcto' : 'Error'}</span>;
  }, []);

  const renderOrderCell = useCallback(
    (cellData) => {
      const ordId: number = cellData.data?.Mtsm_Ord_Id;
      const ordNumero = cellData.data?.Orden?.Ord_Numero ?? ordId;
      return (
        <div className='trace-masive-order-cell' onClick={() => handleOrderClick(ordId)}>
          {ordNumero}
        </div>
      );
    },
    [handleOrderClick]
  );

  return (
    <div className={'view crm-contact-list'}>
      <div className={'view-wrapper view-wrapper-contact-list'}>
        <div className='view-wrapper-mass-validation' style={{ height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <DataGrid height={'100%'} className='grid' ref={gridRef} dataSource={data} rowAlternationEnabled={true}>
          <LoadPanel enabled={true} />
          <SearchPanel visible placeholder='Buscar' />
          <HeaderFilter visible={true} />
          <Paging enabled={true} defaultPageSize={100} />
          <Pager visible={true} allowedPageSizes={[100, 200, 400]} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
          <Toolbar>
            <Item location='before'>
              <div className='grid-header' style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Trazabilidad tareas masivas</span>
                <span style={{ fontSize: '13px', color: '#666', fontWeight: 'normal' }}>
                  Registros: <strong>{data?.length || 0}</strong>
                </span>
              </div>
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='TraceMasiveRefreshBtn' icon='refresh' text='Actualizar' stylingMode='text' onClick={handleRefresh} />
              <Tooltip target={'#TraceMasiveRefreshBtn'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Refrescar datos
              </Tooltip>
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton' visible={runMenuItems.length > 0}>
              <Button id='RunMasiveTaskBtn' icon={runTask} text='Ejecutar tarea' stylingMode='text' />
              <ContextMenu target='#RunMasiveTaskBtn' dataSource={runMenuItems} displayExpr='text' width='auto' showEvent='dxclick' onItemClick={handleRunTask} />
              <Tooltip target={'#RunMasiveTaskBtn'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Ejecutar tarea masiva
              </Tooltip>
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='TraceMasiveFilterBtn' icon={filter} text='Filtrar' stylingMode='text' onClick={handleModalShowFilter} />
              <Tooltip target={'#TraceMasiveFilterBtn'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Filtrar resultados
              </Tooltip>
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>
          <Column
            dataField='Mtsm_Tipo'
            caption='TIPO'
            width={70}
            alignment='center'
            cellRender={renderTipoCell}
            headerFilter={{
              dataSource: LstTipos.map((t) => ({ text: t.Nombre, value: t.Id })),
            }}
          />
          <Column name='Mtsm_Fecha_Fecha' dataField='Mtsm_Fecha' width={100} alignment='center' dataType='datetime' format={'yyyy/MM/dd'} caption='FECHA' />
          <Column name='Mtsm_Fecha_Hora' dataField='Mtsm_Fecha' width={110} alignment='center' dataType='datetime' format={'hh:mm aa'} caption='HORA' />
          <Column dataField='Orden.Ord_Numero' caption='ORDEN' width={110} alignment='center' cellRender={renderOrderCell} />
          <Column dataField='Mtsm_Destinatario' caption='DESTINATARIO' />
          <Column
            dataField='Mtsm_Estado'
            caption='ESTADO'
            alignment='center'
            width={120}
            cellRender={renderEstadoCell}
            headerFilter={{
              dataSource: [
                { text: 'Correcto', value: true },
                { text: 'Error', value: false },
              ],
            }}
          />
          <Column type='buttons' caption='DETALLE'>
            <ButtonGrid hint='Visualizar' icon='eyeopen' onClick={handlePreview} />
          </Column>
        </DataGrid>
        </div>

        {selectedItem && (
          <PopupInformation visible={!!selectedItem} handleClose={() => setSelectedItem(undefined)} width={700} title='Detalle tarea masiva'>
            <div className='popup-information'>
              <div className='popup-information-content'>
                <div className='popup-information-content-row'>
                  <div className='popup-information-content-row-title'>Fecha:</div>
                  <div className='popup-information-content-row-value'>{moment(selectedItem.Mtsm_Fecha).format('DD/MM/YYYY HH:mm')}</div>
                </div>
                <div className='popup-information-content-row'>
                  <div className='popup-information-content-row-title'>Orden:</div>
                  <div className='popup-information-content-row-value'>{selectedItem.Orden?.Ord_Numero || selectedItem.Mtsm_Ord_Id}</div>
                </div>
                <div className='popup-information-content-row'>
                  <div className='popup-information-content-row-title'>Tipo:</div>
                  <div className='popup-information-content-row-value'>{LstTipos.find((x) => x.Id === selectedItem.Mtsm_Tipo)?.Nombre || 'N/A'}</div>
                </div>
                <div className='popup-information-content-row'>
                  <div className='popup-information-content-row-title'>Destinatario:</div>
                  <div className='popup-information-content-row-value'>{selectedItem.Mtsm_Destinatario || 'N/A'}</div>
                </div>
                <div className='popup-information-content-row'>
                  <div className='popup-information-content-row-title'>Estado:</div>
                  <div className='popup-information-content-row-value'>{selectedItem.Mtsm_Estado ? 'Correcto' : 'Error'}</div>
                </div>
                <div className='popup-information-content-row'>
                  <div className='popup-information-content-row-title'>Mensaje:</div>
                  <div className='popup-information-content-row-value'>{selectedItem.Mtsm_Mensaje || 'N/A'}</div>
                </div>
              </div>
            </div>
          </PopupInformation>
        )}

        <RightPanelControlled isVisible={isPanelOpen} isPinned={false} onClose={handleClosePanel} includePin={false} title='Consulta de Orden'>
          {loadingPanel ? (
            <div style={{ padding: '30px', textAlign: 'center' }}>
              <LoadIndicator visible={true} />
              <div style={{ marginTop: '15px', color: '#666' }}>Cargando información...</div>
            </div>
          ) : currentOrder ? (
            <>
              <OrderInfo orderData={currentOrder} />
              <PatientInfo patientData={currentPatient} />
              <div style={{ padding: '15px', borderTop: '1px solid #e0e0e0', marginBottom: '80px' }}>
                <Button text='IR A LA ORDEN' type='default' stylingMode='contained' width='100%' onClick={handleClickViewOrder} disabled={!currentOrder || loadingFindOrder} />
              </div>
            </>
          ) : (
            <div style={{ padding: '30px', textAlign: 'center', color: '#999' }}>
              <div style={{ fontSize: '16px', marginBottom: '10px' }}>
                <i className='dx-icon-info' style={{ fontSize: '48px', color: '#ddd' }} />
              </div>
              <div>Selecciona una orden para ver sus detalles</div>
            </div>
          )}
        </RightPanelControlled>

        <ModalFilter visible={modalVisible} handleApplyFilter={handleApplyFilter} handleCancelFilter={handleModalHideFilter} />

        <ModalConfirmation
          visible={confirmTask.visible}
          message={`Está seguro que desea ejecutar la tarea "${confirmTask.text || ''}"`}
          loading={confirmLoading}
          onConfirm={handleConfirmTask}
          onCancel={handleCancelConfirmTask}
        />
      </div>
    </div>
  );
};

export default Index;
